import { isPlainObject } from 'lodash';
import type { StateStorage } from '../../types/state-storage';

export const DATAGRID_STORAGE_PREFIX = 'datagrid:';

export interface DataGridLocalStorageRecord {
  version: 1;
  state: StateStorage['state'];
  lastUsedAt: number;
  ttl?: number;
}

export const createDataGridLocalStorageRecord = (
  state: StateStorage['state'],
  ttl?: number,
): DataGridLocalStorageRecord => ({
  version: 1,
  state,
  lastUsedAt: Date.now(),
  ...(ttl === undefined ? {} : { ttl }),
});

export const parseDataGridLocalStorageRecord = (
  value: string | undefined,
): DataGridLocalStorageRecord | undefined => {
  if (!value) return undefined;

  try {
    const parsed: unknown = JSON.parse(value);
    if (!isPlainObject(parsed)) return undefined;
    const record = parsed as Record<string, unknown>;

    // Previous versions stored the grid state directly, without metadata.
    if (!('version' in record)) {
      return createDataGridLocalStorageRecord(parsed as StateStorage['state']);
    }

    if (
      record.version !== 1 ||
      !isPlainObject(record.state) ||
      typeof record.lastUsedAt !== 'number' ||
      !Number.isFinite(record.lastUsedAt) ||
      (record.ttl !== undefined &&
        (typeof record.ttl !== 'number' ||
          !Number.isFinite(record.ttl) ||
          record.ttl <= 0))
    ) {
      return undefined;
    }

    return record as unknown as DataGridLocalStorageRecord;
  } catch {
    return undefined;
  }
};

export const isDataGridLocalStorageRecordExpired = (
  record: DataGridLocalStorageRecord,
) => record.ttl !== undefined && Date.now() - record.lastUsedAt >= record.ttl;

export const readDataGridLocalStorageRecord = (
  value: string | undefined,
): DataGridLocalStorageRecord | undefined => {
  const record = parseDataGridLocalStorageRecord(value);
  return record && !isDataGridLocalStorageRecordExpired(record)
    ? record
    : undefined;
};

/** Also removes records of deleted widgets that will never be opened again. */
export const cleanupDataGridLocalStorageState = () => {
  try {
    const storage = window.localStorage;
    for (let index = storage.length - 1; index >= 0; index--) {
      const key = storage.key(index);
      if (!key?.startsWith(DATAGRID_STORAGE_PREFIX)) continue;
      if (!readDataGridLocalStorageRecord(storage.getItem(key))) {
        storage.removeItem(key);
      }
    }
  } catch {
    // Storage may be unavailable; Mantine still provides state in memory.
  }
};
